import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import isPlainObject from "lodash/isPlainObject";

/**
 * useStorage
 *  - useSessionStorage
 *  - useLocalStorage
 *
 * A simple sessionStorage or localStorage adapter hook for persisting state.
 * The hook will read/write and remove (on unmount) any data to the web storage,
 * it will only read the persisted values on initial mount.
 * It will persist new values when using the returned `setValue` updater function
 *
 * Usage:
 * ```
 * const [value, setValue] = useSessionStorage("my-key", "initialValue");
 * // value = "initialValue"
 *
 * const [value, setValue] = useSessionStorage("my-key");
 * // value = null
 *
 * // Assuming the sessionStorage has ["jui-persist:my-key", "Test 1"]
 * const [value, setValue, removeValue, error] = useSessionStorage("my-key");
 * // value = "Test 1"
 * ```
 */

// TODO: Add support for expiry times using a common interface like { value: x, expiry: Date().now + ttl }
export const isExpired = (expiry: number) => expiry > 0 && expiry < Date.now();
type StorageObject<V extends any> = {
  value: V;
  expires: number;
  version: number;
};

export const toStorage = <ValueType extends any>(
  value: ValueType,
  ttl?: number,
): string => {
  return JSON.stringify({
    value,
    expires: ttl ?? -1,
    version: -1, // to manage migrations down the line?
  });
};
export const fromStorage = <ValueType extends any>(
  data: string | null,
  onExpiry?: Function,
): ValueType | null => {
  // data will be null is the Storage.getItem method found no such key
  if (!data) return null;

  // validate expiry
  try {
    const parsed = JSON.parse(data) as StorageObject<ValueType>;

    if (isExpired(parsed.expires)) {
      if (typeof onExpiry === "function") {
        onExpiry(parsed);
      }
      return null;
    }

    return parsed.value;
  } catch (err) {
    throw new Error(err);
  }
};

export const storePrefix = (key: string) => `jui-persist:${key}`;

type Store = typeof window.sessionStorage | typeof window.localStorage;
type StorageKey = string;
type StorageSetter<T> = T | ((prevState: T) => T);
type StorageOptions = {
  removeOnUnmount?: boolean;
};
type StorageValueAPI<T> = [
  T,
  (value: StorageSetter<T>, ttl?: number) => void,
  () => void,
  Error | null,
];

export const makeStorageHook = (adapter: Store) => {
  const store = adapter;
  // store => key + initialValue + options => value
  function useStorage<T>(
    key: StorageKey,
    initialValue: T,
    options: StorageOptions,
  ): StorageValueAPI<T>;
  // store => key + options => value (default initialValue)
  function useStorage<T>(
    key: StorageKey,
    options: StorageOptions,
  ): StorageValueAPI<T>;
  // store => key + initialValue => value (default options)
  function useStorage<T>(key: StorageKey, initialValue: T): StorageValueAPI<T>;
  // store => key => value (default everywhere)
  function useStorage<T>(key: StorageKey): StorageValueAPI<T>;

  // real implementation with optional args to construct our function from given
  // the varying call signatures
  function useStorage<T>(
    k: StorageKey,
    ...args: Array<StorageSetter<T> | StorageOptions>
  ): StorageValueAPI<T> {
    // Derive stable internal values from optional arguments:
    // args[0] can be any | options (must check for known keys if object to identify if options or initialValue)
    // args[1] can only be options if set
    const key = useMemo(() => storePrefix(k), [k]);

    // Only run once on init
    const argsRef = useRef(args);
    argsRef.current = args;

    const [initialValue, options] = useMemo(() => {
      const fnArgs = argsRef.current;

      // Default Value
      // @ts-ignore
      let initialValue: T = null;
      // Default Options
      let options: StorageOptions = {
        removeOnUnmount: false,
      };
      // used to check if the arg is options object or just arbitrary object to be stored
      const ALLOWED_OPTIONS = Object.keys(options);

      // if args has 2 things, last is always options
      if (fnArgs.length === 2) {
        initialValue = fnArgs[0] as T;
        options = fnArgs[1] as StorageOptions;
      }
      // if fnArgs has only 1 thing, it can be either the generic value (any type) OR
      // it can be the options so we need to check for a known key's existence to
      // determine if we are in an options object or not.
      if (fnArgs.length === 1) {
        const isOptionsObject = (
          defaultOptionsKeys: string[],
          keys: string[],
        ) => !!keys.filter((key) => defaultOptionsKeys.includes(key)).length;
        // Test for known option keys
        if (
          isPlainObject(fnArgs[0]) &&
          isOptionsObject(ALLOWED_OPTIONS, Object.keys(fnArgs[0] as object))
        ) {
          options = fnArgs[0] as StorageOptions;
        } else {
          initialValue = fnArgs[0] as T;
        }
      }

      return [initialValue, options];
    }, [argsRef]);

    // Capture internal state
    const [error, setError] = useState<null | Error>(null);
    const [storedValue, setStoredValue] = useState<T>(() => {
      try {
        // Populate initial state from storage, fallback to initialValue if needed
        const item = fromStorage<T>(store.getItem(key), () => {
          store.removeItem(key);
        });
        return item ?? initialValue;
      } catch (err) {
        setError(err);
        return initialValue;
      }
    });

    // Create consumer API
    const storedValueRef = useRef(storedValue);
    storedValueRef.current = storedValue;
    const setValue = useCallback(
      (value: StorageSetter<T>, ttl?: number) => {
        try {
          // Resolve value if function
          const storableValue =
            value instanceof Function ? value(storedValueRef.current) : value;
          // Persist
          store.setItem(key, toStorage(storableValue, ttl));
          const stored = fromStorage(store.getItem(key));
          // Update UI
          setStoredValue(stored as T);
        } catch (err) {
          setError(err);
        }
      },
      [key, storedValueRef],
    );

    const removeValue = useCallback(() => {
      store.removeItem(key);
    }, [key]);

    // Cleanup if the options.removeOnUnmount is set
    useEffect(() => (options.removeOnUnmount ? removeValue : undefined), [
      options.removeOnUnmount,
      removeValue,
    ]);

    // Expose consumer API
    return useMemo(() => {
      return [storedValue, setValue, removeValue, error];
    }, [storedValue, setValue, removeValue, error]);
  }

  return useStorage;
};
